/*
 * HTML/XML helpers for the page renderer: escaping, JSON-LD, and filling the
 * built UI's index.html with per-page head tags and body markup.
 *
 * Every string that reaches markup goes through esc(); everything passed to
 * String.prototype.replace is a function so "$&" in a lawyer's name or an
 * article cannot be read as a replacement pattern.
 */
const { toHref } = require("./urls");

const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

function esc(value) {
  return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ESCAPES[c]);
}

/** JSON for a <script type="application/ld+json">: no "<" can close the tag. */
function jsonLd(data) {
  const json = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  return `<script type="application/ld+json">${json}</script>`;
}

/** <a> to a decoded site path. */
function link(path, text, attrs = "") {
  return `<a href="${esc(toHref(path))}"${attrs ? ` ${attrs}` : ""}>${esc(text)}</a>`;
}

// Head tags the template ships with that each page replaces with its own.
const REPLACED_META = /<meta\b[^>]*\b(?:name|property)\s*=\s*["'](?:description|robots|og:[^"']*|twitter:[^"']*)["'][^>]*>\s*/gi;
const REPLACED_LINKS = /<link\b[^>]*\brel\s*=\s*["'](?:canonical|alternate)["'][^>]*>\s*/gi;
const TITLE = /<title\b[^>]*>[\s\S]*?<\/title>\s*/gi;
const HEAD_MARKER = "<!--seo-head-->";

/**
 * Fill the template.
 *   lang      page language (sets <html lang dir>)
 *   head      tags to inject (already escaped)
 *   body      markup to put inside <div id="root"> (already escaped)
 *   keepHead  leave the template's own title/meta alone (private pages)
 */
function fillTemplate(template, { lang, head = "", body = "", keepHead = false }) {
  let html = template;
  if (lang) {
    const dir = lang === "ar" ? "rtl" : "ltr";
    html = html.replace(/<html\b([^>]*)>/i, (match, attrs) => {
      const rest = attrs.replace(/\s+(?:lang|dir)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
      return `<html${rest} lang="${lang}" dir="${dir}">`;
    });
  }
  if (!keepHead) {
    html = html.replace(/<head\b[^>]*>[\s\S]*?<\/head>/i, (section) => section
      .replace(TITLE, "")
      .replace(REPLACED_META, "")
      .replace(REPLACED_LINKS, (tag) => (/hreflang/i.test(tag) || /canonical/i.test(tag) ? "" : tag)));
  }
  if (html.includes(HEAD_MARKER)) {
    html = html.replace(HEAD_MARKER, () => head);
  } else {
    html = html.replace(/<\/head>/i, () => `${head}</head>`);
  }
  if (body) {
    html = html.replace(/<div\s+id\s*=\s*(["'])root\1\s*>\s*<\/div>/i, () => `<div id="root">${body}</div>`);
  }
  return html;
}

// Markup an editorial answer may keep in server-rendered pages.
const SAFE_TAGS = new Set(["p", "ul", "ol", "li", "strong", "em", "b", "i", "h3", "h4", "br"]);

/**
 * HTML reduced to SAFE_TAGS without any attribute; other tags are dropped
 * (script and style with their content) and stray angle brackets escaped.
 */
function safeHtml(html) {
  const source = String(html == null ? "" : html).replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, " ");
  return source.split(/(<[^>]*>)/).map((part) => {
    const tag = /^<(\/?)([a-z][a-z0-9]*)\b[^>]*>$/i.exec(part);
    if (tag) {
      const name = tag[2].toLowerCase();
      if (!SAFE_TAGS.has(name)) return " ";
      return name === "br" ? (tag[1] ? "" : "<br>") : `<${tag[1]}${name}>`;
    }
    return part.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }).join("");
}

const XML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" };
function xmlEsc(value) {
  return String(value == null ? "" : value).replace(/[&<>"']/g, (c) => XML_ESCAPES[c]);
}

module.exports = { esc, jsonLd, link, fillTemplate, xmlEsc, safeHtml };
